<script setup lang="ts">
import { CircleAlert } from 'lucide-vue-next';
import { computed, onMounted } from 'vue';

import { useMicrosoftConnectionStore } from '@/stores/microsoftConnectionStore';

const props = withDefaults(
    defineProps<{
        feature: 'sharepoint' | 'azure_groups';
        compact?: boolean;
    }>(),
    { compact: false }
);

const microsoftConnectionStore = useMicrosoftConnectionStore();

const notice = computed(() => {
    const health = microsoftConnectionStore.health;
    if (!health) return null;

    if (props.feature === 'sharepoint') {
        if (health.status === 'healthy' || health.status === 'not_configured')
            return null;
        return {
            title: 'SharePoint connection',
            detail: health.detail,
            isWarning: health.status === 'unavailable'
        };
    }

    // The check reads SharePoint sites: a missing SharePoint permission says nothing about
    // group permissions, only an invalid secret breaks both.
    if (health.status !== 'invalid_secret') return null;
    return {
        title: 'Azure AD groups',
        detail: 'Access granted through these groups is suspended: the client secret is invalid or expired. Ask an administrator to update it.',
        isWarning: false
    };
});

onMounted(() => microsoftConnectionStore.ensureLoaded());
</script>

<template>
    <div
        v-if="notice"
        role="status"
        class="flex items-center border text-sm"
        :class="[
            compact
                ? 'gap-2 rounded-md px-3 py-2'
                : 'flex-wrap justify-between gap-3 rounded-lg p-4',
            notice.isWarning
                ? 'border-yellow25 bg-yellow25/30 text-yellow100 dark:text-foreground'
                : 'border-destructive/40 bg-destructive/10 text-destructive'
        ]"
    >
        <CircleAlert v-if="compact" class="h-4 w-4 shrink-0" />
        <p v-if="compact">
            <span class="font-semibold">{{ notice.title }}:</span>
            {{ notice.detail }}
        </p>
        <div v-else>
            <p class="font-semibold">{{ notice.title }}</p>
            <p>{{ notice.detail }}</p>
        </div>
    </div>
</template>
